import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronsUpDown, Search as SearchIcon } from 'lucide-react';
import { useDebounce } from 'rooks';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

const Search = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTags, setSelectedTags] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('q') || '';
  });
  const [searchInput, setSearchInput] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('q') || '';
  });
  const setSearchQueryDebounced = useDebounce(setSearchQuery, 400);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isCoach = params.get('isCoach') === 'true';
    setSelectedTags(isCoach ? ['isCoach'] : []);
  }, [location.search]);

  useEffect(() => {
    if (isDropdownOpen) {
      const params = new URLSearchParams(location.search);
      const isCoach = params.get('isCoach') === 'true';
      setSelectedTags(isCoach ? ['isCoach'] : []);
    }
  }, [isDropdownOpen, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (searchQuery) {
      params.set('q', searchQuery);
    } else {
      params.delete('q');
    }
    navigate({ search: params.toString() });
  }, [searchQuery, location.search, navigate]);

  const handleTagChange = (tag) => {
    setSelectedTags((prevTags) =>
      prevTags.includes(tag)
        ? prevTags.filter((t) => t !== tag)
        : [...prevTags, tag]
    );
  };

  const clearFilters = () => {
    navigate({ search: '' });
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    selectedTags.forEach((tag) => params.set(tag, 'true'));
    navigate({ search: params.toString() });
    setIsDropdownOpen(false);
  };

  return (
    <div className="flex gap-4 w-full mb-4">
      <div className="relative flex-1">
        <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search Customers"
          className="pl-10"
          autoFocus
          onChange={(e) => {
            setSearchInput(e.target.value);
            setSearchQueryDebounced(e.target.value);
          }}
          value={searchInput}
        />
      </div>
      <div className="flex gap-2">
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="min-w-[100px] flex justify-between items-center"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              Filters
              <ChevronsUpDown className="h-4 w-4 opacity-50 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[280px] p-4">
            <div className="text-lg font-medium mb-2">Filter by</div>
            <Button
              onClick={clearFilters}
              variant="ghost"
              size="sm"
              className="text-secondary p-0 hover:bg-transparent mb-2"
            >
              Clear Filter
            </Button>
            <div className="space-y-3">
              <DropdownMenuCheckboxItem
                checked={selectedTags.includes('isCoach')}
                onCheckedChange={() => handleTagChange('isCoach')}
                onSelect={(e) => e.preventDefault()}
                className="cursor-pointer data-[state=checked]:bg-muted"
              >
                Is Coach
              </DropdownMenuCheckboxItem>
            </div>
            <DropdownMenuSeparator className="my-4" />
            <Button onClick={applyFilters} variant="outline" className="w-full">
              Apply Filter
            </Button>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default Search;
